import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import classNames from "classnames"
import styled from "styled-components"

import { useAppSelector } from "store/hooks"
import { contractorThemeSelector, userLogoSelector } from "store/tempDataSlise"
import { ContractorThemes } from "constants/ContractorThemes"
import { ContractorUserRole } from "types/ContractorUserEnum"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import styles from "assets/scss/index.module.scss"
import logo from "assets/svg/logo-white.svg"
import { ReactComponent as IconAlerts } from "assets/svg/menu-alerts.svg"
import { ReactComponent as IconCustomers } from "assets/svg/menu-customers.svg"
import { ReactComponent as IconDashboard } from "assets/svg/menu-dashboard.svg"
import { ReactComponent as IconMessages } from "assets/svg/menu-messages.svg"
import { ReactComponent as IconMonitoring } from "assets/svg/menu-monitoring.svg"
import { ReactComponent as IconOrders } from "assets/svg/menu-orders.svg"
import { ReactComponent as IconPerformance } from "assets/svg/menu-performance.svg"

import { isRestrictedSubscriptionPlan } from "utils/isRestrictedSubscriptionPlan"
import { useNewOrdersQuantity } from "hooks/useNewOrdersQuantity"
import { Hamburger } from "components/common/Hamburger"
import MenuWrapper from "components/common/MenuWrapper"
import { NavLink } from "components/common/NavLink"
import { UserMenu } from "components/common/UserMenu"

import { RightSideIconsWrapper } from "../styled"
import { UniversityIcon } from "./UniversityIcon"

const StyledHeader = styled.header<{ backgroundStyle: string; isSticky: boolean }>`
  ${({ isSticky, backgroundStyle }) => isSticky && `background: ${backgroundStyle} !important;`}
`

const LogoWrapper = styled(Link)`
  margin-top: 2px;
  align-self: baseline;
  min-width: 100px;
`

const StyledLogo = styled.img`
  top: 2px;
  position: absolute;
  min-height: 64px;
  max-height: 80px;
  object-fit: contain;
`

export const ContractorTopbar = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const isTechnician = userProfile?.type === ContractorUserRole.TECHNICIAN
  const { data } = useNewOrdersQuantity({ enabled: !isTechnician })
  const [isSticky, setSticky] = useState(false)
  const [isOpen, setOpen] = useState(false)
  const current_subscription = userProfile?.contractor?.subscription_plan
  const hasEPlusPlan = current_subscription === SubscriptionPlan.E_PLUS
  const universityAccessible = hasEPlusPlan || userProfile?.contractor?.university_accessible || false
  const isRestricted = isRestrictedSubscriptionPlan(userProfile)
  const shouldHideTopBar = isRestricted && universityAccessible
  const currentTheme = useAppSelector(contractorThemeSelector)
  const userLogo = useAppSelector(userLogoSelector)

  const closeMenu = () => {
    setOpen(false)
  }

  const handleScroll = () => {
    const distance = window.scrollY

    if (distance > 0 && !isSticky) {
      setSticky(true)
    } else if (distance === 0 && isSticky) {
      setSticky(false)
    }
  }

  useEffect(() => {
    const setStickyOnScroll = () => handleScroll()

    window.addEventListener("scroll", setStickyOnScroll)

    return () => {
      window.removeEventListener("scroll", setStickyOnScroll)
    }
  }, [isSticky])

  const getBackgroundStyle = (hasEPlusPlan: boolean) => {
    if (hasEPlusPlan) {
      return currentTheme.backgroundGradient
    }
    return ContractorThemes.default.backgroundGradient
  }

  return (
    <StyledHeader
      backgroundStyle={getBackgroundStyle(hasEPlusPlan)}
      isSticky={isSticky}
      className={classNames(styles.topbar, { [styles.sticky]: isSticky })}
    >
      <LogoWrapper className={styles.logoWrapper} to="/">
        <StyledLogo className={styles.logo} src={userLogo || logo} alt="logo" />
      </LogoWrapper>
      {!shouldHideTopBar && (
        <>
          <MenuWrapper backgroundStyle={getBackgroundStyle(hasEPlusPlan)} isOpen={isOpen} onClose={closeMenu}>
            {!isTechnician && (
              <NavLink to="/dashboard" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
                <IconDashboard className={styles.menuIcon} />
                Dashboard
              </NavLink>
            )}
            <NavLink to="/customers" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
              <IconCustomers className={styles.menuIcon} />
              Customers
            </NavLink>
            {!isTechnician && (
              <NavLink to="/orders" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
                <IconOrders className={styles.menuIcon} />
                Orders
                {Boolean(data?.data?.quantity) && <span className={styles.redDot}>{data?.data?.quantity}</span>}
              </NavLink>
            )}
            {!isTechnician && (
              <NavLink to="/messages" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
                <IconMessages className={styles.menuIcon} />
                Messages
              </NavLink>
            )}
            <NavLink to="/alerts" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
              <IconAlerts className={styles.menuIcon} />
              Alerts
            </NavLink>
            {!isTechnician && (
              <NavLink to="/perf-test" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
                <IconPerformance className={classNames(styles.menuIcon, styles.menuIconPerformance)} />
                Performance Test
              </NavLink>
            )}
            <NavLink to="/monitoring" className={styles.menuItem} activeClassName={styles.active} onClick={closeMenu}>
              <IconMonitoring className={styles.menuIcon} />
              Monitoring
            </NavLink>
          </MenuWrapper>
          <RightSideIconsWrapper>
            <Hamburger onClick={() => setOpen(!isOpen)} isOpen={isOpen} />
            <UniversityIcon />
            <UserMenu />
          </RightSideIconsWrapper>
        </>
      )}
    </StyledHeader>
  )
}
